<template>
    <div class="full-menu" :class="{ 'no-hover': !isStylePreview && !isPreview, 'inline-edit-mode': enableInlineEdit }" :style="densityVars">
        <div class="frame" :style="frameStyles">
            <div v-if="showBackButton" class="back-btn" :style="backBtnStyles">
                <div class="back-icon-wrapper" :style="backIconStyles">
                    <component :is="icons.back2" class="back-icon" :color="backIconColor" />
                </div>
            </div>

            <div class="title-card" :style="titleCardStyles">
                <h1
                    ref="titleTextRef"
                    v-show="true"
                    class="splash-title editable-text"
                    :class="{ 'is-editable': enableInlineEdit }"
                    :style="titleStyles"
                    :contenteditable="enableInlineEdit"
                    @blur="updateTitle"
                    @input="(e) => handleTextFieldInput('titleText', e)"
                    @paste.prevent="handlePaste"
                    @mousedown="handleEditableMouseDown"
                    @keydown.enter.prevent="handleEnter"
                    suppressContentEditableWarning
                >{{ displayTitleText }}</h1>
                <div v-if="!isTitleHidden() && !isSubtextHidden()" class="title-divider" :style="titleDividerStyles"></div>
                <p
                    ref="subtextRef"
                    v-show="true"
                    class="body editable-text"
                    :class="{ 'is-editable': enableInlineEdit }"
                    :style="bodyStyles"
                    :contenteditable="enableInlineEdit"
                    @blur="updateSubtext"
                    @input="(e) => handleTextFieldInput('subtext', e)"
                    @paste.prevent="handlePaste"
                    @mousedown="handleEditableMouseDown"
                    @keydown.enter.prevent="handleEnter"
                    suppressContentEditableWarning
                >{{ displaySubtext }}</p>
            </div>

            <div class="buttons" :style="buttonsGridStyles">
                <div
                    v-for="(btn, i) in buttons"
                    :key="i"
                    v-show="btn.enabled !== false"
                    class="btn editable-text"
                    :class="{
                        'is-editable': enableInlineEdit,
                        'btn-disabled': btn.enabled === false
                    }"
                    :style="getButtonStyles(btn)"
                    :ref="`buttonRef${i}`"
                    :contenteditable="enableInlineEdit && btn.enabled !== false"
                    @blur="(e) => updateButton(i, e)"
                    @input="(e) => handleTextFieldInput(`buttonText${i}`, e)"
                    @paste.prevent="handlePaste"
                    @mousedown="handleEditableMouseDown"
                    @keydown.enter.prevent="handleEnter"
                    suppressContentEditableWarning
                >{{ getDisplayButtonText(i) }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import icons from './menu-icons';
import menuMixin from './menu-mixin';
import menuReactivityMixin from './menu-reactivity-mixin';
const Back2SVG = require('!raw-loader!../assets/icons/back2.svg');

export default {
    name: "MenuTwoToSixOptionFullScreen",
    mixins: [menuMixin, menuReactivityMixin],
    props: {
        styleData: {type: Object, default: () => ({})},
        menuData: {type: Object, default: () => ({})},
        isStylePreview: {type: Boolean, default: false},
        isPreview: {type: Boolean, default: false},
        enableInlineEdit: {type: Boolean, default: false}
    },
    data() {
        return {
            icons: icons
        };
    },
    computed: {
        titleText() {
            return this.cleanText(this.menuData.titleText);
        },
        displayTitleText() {
            const text = this.titleText;
            if (!text) {
                if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                    return "Menu Title Goes Here";
                }
                return this.enableInlineEdit ? ' ' : '';
            }
            return text;
        },
        subtext() {
            return this.cleanText(this.menuData.subtext);
        },
        displaySubtext() {
            const text = this.subtext;
            if (!text) {
                if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                    return "This is where your menu description text will appear. You can provide instructions, information, or any other content you want to display to users.";
                }
                return this.enableInlineEdit ? ' ' : '';
            }
            return text;
        },
        buttons() {
            const src = this.menuData.buttons || [];
            const fallback = [
                {text: "", enabled: true},
                {text: "", enabled: true},
                {text: "", enabled: false},
                {text: "", enabled: false},
                {text: "", enabled: false},
                {text: "", enabled: false}
            ];
            let buttons = (src.length ? src : fallback).slice(0, 6);

            if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                const defaultTexts = ["Button 1", "Button 2", "Button 3", "Button 4", "Button 5", "Button 6"];
                return buttons.map((btn, index) => {
                    const trimmedText = (btn.text && btn.text.trim()) || "";
                    return {
                        ...btn,
                        text: trimmedText || (btn.enabled !== false ? defaultTexts[index] : "")
                    };
                });
            }

            return buttons;
        },
        visibleButtonTexts() {
            if (!this.buttons || !Array.isArray(this.buttons)) {
                return [];
            }
            return this.buttons.map(btn => {
                const cleaned = this.cleanText(btn?.text);
                return cleaned || (this.enableInlineEdit ? ' ' : '');
            });
        },
        showBackButton() {
            return this.menuData.backButton?.enabled !== false;
        },

        densityVars() {
            if (!this.isStylePreview) return {};
            return {
                "--base-font": "0.85rem",
                "--title-scale": "1.6",
                "--body-scale": "1.1",
                "--btn-scale": "1.0",
                "--element-scale": "0.7",
                "--gap-scale": "0.6"
            };
        },

        effective() {
            const d = {
                titleScreenBackgroundColor: {r: 44, g: 62, b: 80, a: 1},
                titleColor: {r: 255, g: 255, b: 255, a: 1},
                textColor: {r: 235, g: 244, b: 248, a: 0.95},
                buttonBackgroundColor: {r: 33, g: 122, b: 145, a: 0.95},
                buttonHoverColor: {r: 52, g: 192, b: 32, a: 1},
                buttonTextColor: {r: 255, g: 255, b: 255, a: 1},
                outlineColor: {r: 255, g: 255, b: 255, a: 0.9},
                opacity: 1.0
            };
            return {...d, ...this.styleData};
        },

        frameStyles() {
            const backW = this.isStylePreview ? "calc(66px * var(--element-scale, 1))" : "75px";
            const rowGap = this.isStylePreview ? "calc(16px * var(--gap-scale, 1))" : "10px";
            const columnGap = this.isStylePreview ? "calc(16px * var(--gap-scale, 1))" : "16px";
            const margin = this.isStylePreview ? "calc(16px * var(--gap-scale, 1)) auto" : "16px";
            const maxWidth = this.isStylePreview ? "calc(980px * var(--max-width-scale, 1))" : "980px";
            return {
                display: "grid",
                gridTemplateColumns: `${backW} 1fr`,
                columnGap,
                rowGap,
                width: "100%",
                maxWidth,
                margin
            };
        },

        backBtnStyles() {
            const width = this.isStylePreview ? "calc(56px * var(--element-scale, 1))" : "75px";
            const height = this.isStylePreview ? "calc(44px * var(--element-scale, 1))" : "56px";
            const fontSize = this.isStylePreview ? "calc(24px * var(--element-scale, 1))" : "24px";
            const borderWidth = this.isStylePreview ? "calc(3px * var(--element-scale, 1))" : "3px";
            const base = {
                width: width,
                height: height,
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: fontSize,
                fontWeight: "bold",
                border: `${borderWidth} solid`,
                boxShadow: "0 4px 10px rgba(0,0,0,0.35)",
                transition: 'background-color .18s ease, box-shadow .18s ease'
            };
            return {
                ...base,
                backgroundColor: this.rgba(this.effective.buttonBackgroundColor),
                color: this.rgba(this.effective.textColor, false),
                borderColor: this.rgba(this.effective.outlineColor, false),
                '--hover-bg': this.rgba(this.effective.buttonHoverColor)
            };
        },
        backIconStyles() {
            return this.getIconStylesFromSvg(Back2SVG);
        },
        backIconColor() {
            const effective = this.getEffectiveStyle();
            return this.rgba(effective.textColor, false);
        },

        titleCardStyles() {
            const padding = this.isStylePreview ? "calc(12px * var(--element-scale, 1)) calc(14px * var(--element-scale, 1))" : "18px 20px";
            const radius = this.isStylePreview ? "calc(12px * var(--element-scale, 1))" : "16px";
            const borderWidth = this.isStylePreview ? "calc(3px * var(--element-scale, 1))" : "3px";
            return {
                gridColumn: "2 / -1",
                padding,
                borderRadius: radius,
                border: `${borderWidth} solid`,
                boxShadow: "0 8px 18px rgba(0,0,0,0.35)",
                backgroundColor: this.rgba(this.effective.titleScreenBackgroundColor),
                borderColor: this.rgba(this.effective.outlineColor, false)
            };
        },
        titleStyles() {
            const lineHeight = 1.24;
            const fontSize = "calc(var(--base-font) * var(--title-scale))";
            const marginBottom = this.isStylePreview ? "calc(8px * var(--gap-scale, 1))" : "12px";
            const hidden = this.isTitleHidden();
            return {
                margin: `0 0 ${marginBottom} 0`,
                fontWeight: 800,
                fontSize: fontSize,
                lineHeight: lineHeight,
                letterSpacing: "0.4px",
                color: this.rgba(this.effective.titleColor, false),
                visibility: hidden ? 'hidden' : 'visible',
                pointerEvents: hidden ? 'none' : 'auto'
            };
        },

        bodyStyles() {
            const lineHeight = 1.65;
            const fontSize = "calc(var(--base-font) * var(--body-scale))";
            const lines = this.isStylePreview ? 4 : 7;
            const hidden = this.isSubtextHidden();
            return {
                margin: 0,
                fontSize: fontSize,
                lineHeight: lineHeight,
                color: this.rgba(this.effective.textColor, false),
                minHeight: `calc(${fontSize} * ${lineHeight} * ${lines})`,
                maxHeight: `calc(${fontSize} * ${lineHeight} * ${lines})`,
                visibility: hidden ? 'hidden' : 'visible',
                pointerEvents: hidden ? 'none' : 'auto'
            };
        },
        titleDividerStyles() {
            const height = this.isStylePreview ? "calc(2px * var(--element-scale, 1))" : "2px";
            const marginBottom = this.isStylePreview ? "calc(8px * var(--gap-scale, 1))" : "12px";
            return {
                width: '100%',
                height: height,
                backgroundColor: this.rgba(this.effective.titleColor, false),
                margin: `0 0 ${marginBottom} 0`
            };
        },

        buttonsGridStyles() {
            const gap = this.isStylePreview ? "calc(8px * var(--gap-scale, 1)) calc(10px * var(--gap-scale, 1))" : "14px 18px";
            return {
                gridColumn: "2 / -1",
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap,
                alignItems: "stretch",
                width: "100%"
            };
        }
    },
    watch: {
        displayTitleText(newVal) {
            this.syncEditableText('titleTextRef', newVal);
        },
        displaySubtext(newVal) {
            this.syncEditableText('subtextRef', newVal);
        },
        visibleButtonTexts: {
            handler(newTexts, oldTexts) {
                if (this.enableInlineEdit && Array.isArray(newTexts)) {
                    this.$nextTick(() => {
                        newTexts.forEach((displayText, index) => {
                            const btn = this.buttons[index];
                            if (btn && btn.enabled !== false) {
                                if (!oldTexts || oldTexts[index] !== displayText) {
                                    this.syncButtonTextContent(index, displayText);
                                }
                            }
                        });
                    });
                }
            }
        }
    },
    methods: {
        getButtonStyles(btn) {
            const minH = this.isStylePreview ? "calc(48px * var(--element-scale, 1))" : "64px";
            const pad = this.isStylePreview ? "calc(8px * var(--element-scale, 1)) calc(12px * var(--element-scale, 1))" : "10px 16px";
            const radius = this.isStylePreview ? "calc(10px * var(--element-scale, 1))" : "12px";
            const borderWidth = this.isStylePreview ? "calc(3px * var(--element-scale, 1))" : "3px";

            const hidden = this.isButtonHidden(btn);
            if (hidden) {
                return {minHeight: minH, borderRadius: radius, visibility: "hidden", pointerEvents: "none"};
            }

            return {
                minHeight: minH,
                padding: pad,
                borderRadius: radius,
                border: `${borderWidth} solid`,
                fontSize: "calc(var(--base-font) * var(--btn-scale))",
                fontWeight: 800,
                letterSpacing: "0.3px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                cursor: "pointer",
                boxShadow: "0 8px 18px rgba(0,0,0,0.35)",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                backgroundColor: this.rgba(this.effective.buttonBackgroundColor),
                color: this.rgba(this.effective.buttonTextColor, false),
                WebkitTextFillColor: this.rgba(this.effective.buttonTextColor, false),
                borderColor: this.rgba(this.effective.outlineColor, false),
                textShadow: "0 1px 0 rgba(0,0,0,0.35)",
                transition: "background-color .18s ease, box-shadow .18s ease",
                "--hover-bg": this.rgba(this.effective.buttonHoverColor)
            };
        },

        getDisplayButtonText(index) {
            return this.visibleButtonTexts[index] || '';
        },
        updateTitle(event) {
            this.updateTextField('titleText', event);
        },

        updateSubtext(event) {
            this.updateTextField('subtext', event);
        },

        updateButton(index, event) {
            this.updateButtonText(index, event, 'buttons');
        },

        isTitleHidden() {
            if (this.isStylePreview || this.isPreview || this.enableInlineEdit) {
                return false;
            }
            return this.isEmptyText(this.menuData.titleText);
        },

        isSubtextHidden() {
            if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                return false;
            }
            return this.isEmptyText(this.menuData.subtext);
        },

        isButtonHidden(btn) {
            if ((this.isStylePreview || this.isPreview) && !this.enableInlineEdit) {
                return false;
            }
            if (btn.enabled === false || !btn.text || (typeof btn.text === 'string' && btn.text.trim().length === 0)) {
                return true;
            }
            return false;
        }
    }
};
</script>

<style scoped>
.full-menu {
    width: 100%;
    height: 100%;
    background: transparent;
    box-sizing: border-box;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    --base-font: 1rem;
    --title-scale: 2.2;
    --body-scale: 1.125;
    --btn-scale: 1.125;
    --element-scale: 1.0;
    --gap-scale: 1.0;
}

.frame {
    box-sizing: border-box;
    overflow: hidden;
    align-self: center;
}

.title-card {
    box-sizing: border-box;
    overflow: hidden;
}

.buttons {
    box-sizing: border-box;
}

.splash-title {
    display: block;
}

.v-application .title {
    font-size: inherit;
}

.btn {
    position: relative;
}

.btn:nth-child(odd):not(:nth-child(5))::after {
    content: "";
    position: absolute;
    right: calc(-1 * 14px / 2);
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
    height: 70%;
    background: rgba(255, 255, 255, 0.25);
}

.full-menu .btn {
    transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease;
}

.full-menu .btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 22px rgba(0, 0, 0, 0.4);
    background-color: var(--hover-bg) !important;
}

.full-menu:not(.no-hover) .btn:hover {
    background-color: var(--hover-bg) !important;
}

.full-menu .back-btn {
    transition: transform .18s ease, box-shadow .18s ease, background-color .18s ease;
}

.full-menu .back-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
    background-color: var(--hover-bg) !important;
}

.full-menu:not(.no-hover) .back-btn:hover {
    background-color: var(--hover-bg) !important;
}

@media (max-width: 720px) {
    .buttons {
        grid-template-columns: 1fr !important;
    }
}

.editable-text.is-editable {
    cursor: text;
    position: relative;
    outline: none;
}

.editable-text.is-editable:hover {
    background-color: rgba(255, 255, 255, 0.05);
    box-shadow: 0 0 0 2px rgba(150, 150, 150, 0.4);
}

.editable-text.is-editable:focus {
    background-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 0 2px rgba(150, 150, 150, 0.7);
}

.splash-title {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
}

.body {
    display: -webkit-box !important;
    -webkit-line-clamp: 7 !important;
    line-clamp: 7 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
}

.btn {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    overflow: hidden !important;
    word-break: break-word !important;
    white-space: normal !important;
    min-height: 64px !important;
    max-height: none !important;
    box-sizing: border-box !important;
}

.btn .btn-text {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    text-align: center !important;
    width: 100% !important;
}

.btn[contenteditable="true"] {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
}

.btn[contenteditable="true"] .btn-text {
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    word-break: break-word !important;
    text-align: center !important;
    width: 100% !important;
}

.btn-disabled {
    visibility: hidden !important;
    pointer-events: none !important;
}

.back-icon-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
}

.back-icon {
    width: 100%;
    height: 100%;
    object-fit: contain;
    opacity: 0; /* Hide the actual SVG, we'll use the mask instead */
}
</style>
